<template>
	<div class="opportunities-table">
		<header>
			<div class="toolbar">
				<div v-if="canEditColumns" ref="columnSelectorWrapperRef" class="column-selector-anchor">
					<Button
						variant="tertiary"
						size="xs"
						shape="pill"
						class="column-selector-toggle"
						@click="toggleColumnSelector"
					>
						{{ t("opportunities-table.column-selector.title")
						}}<span class="count">{{ visibleColumnKeys.length }}</span>
					</Button>
					<ColumnSelector
						v-if="columnSelectorOpen"
						:columns="selectorColumns"
						:default-columns="defaultSelectorColumns"
						:locked-groups="[GROUP_TABLE]"
						:groups-reorderable="false"
						:cross-group-move="true"
						:title="t('opportunities-table.column-selector.title')"
						:group-labels="groupLabels"
						:note="isInCcaProject ? t('opportunities-table.column-selector.shared-note') : undefined"
						@save="handleColumnSave"
						@close="columnSelectorOpen = false"
					/>
				</div>
				<div ref="sortWrapperRef" class="sort-wrapper">
					<Button
						variant="tertiary"
						size="xs"
						shape="pill"
						class="sort-toggle"
						:class="{ active: sortLevels.length > 0 }"
						@click="toggleSortPanel"
					>
						↕ {{ t("opportunities-table.sort-button")
						}}<span v-if="sortLevels.length > 0" class="count">{{ sortLevels.length }}</span>
					</Button>
					<SortPanel
						v-if="showSortPanel"
						:options="sortOptions"
						:model-value="sortLevels"
						@update:model-value="handleSortLevelsUpdate"
						@close="showSortPanel = false"
					/>
				</div>
				<div ref="filtersWrapperRef" class="filters-wrapper">
					<Button
						variant="tertiary"
						size="xs"
						shape="pill"
						class="filters-toggle"
						:class="{ active: advancedFiltersRef?.hasActiveFilters() }"
						@click="toggleFiltersPanel"
					>
						{{ t("opportunities.tabs.filters")
						}}<span v-if="advancedFiltersRef?.activeFiltersCount" class="count">{{
							advancedFiltersRef.activeFiltersCount
						}}</span>
					</Button>
					<TableFloatingFilterPanel
						v-show="filtersPanelOpen"
						:title="t('layers.sections.opportunities')"
						@close="filtersPanelOpen = false"
					>
						<template v-if="advancedFiltersRef?.hasActiveFilters()" #header-actions>
							<Button
								variant="ghost"
								size="xs"
								shape="pill"
								class="reset-filters-button"
								@click="advancedFiltersRef?.resetFilters()"
							>
								{{ t("filters.reset-to-default") }}
							</Button>
						</template>
						<OpportunitiesTableFilters ref="advancedFiltersRef" :broker-name-sources="brokerNameSources" />
					</TableFloatingFilterPanel>
				</div>
				<TableSearch
					v-model="addressSearch"
					:options="addressOptions"
					:placeholder="t('opportunities-table.address-search-placeholder')"
				/>
			</div>
			<div class="header-right">
				<span class="total">
					<template v-if="currentProject">
						<span class="project-name">{{ projectDisplayName(currentProject, t) }}</span>
						<span class="total-separator">·</span>
					</template>
					<strong>{{ paginationCount }}</strong>
					{{ t(isHuntProject ? "opportunities-table.results-label-targets" : "opportunities-table.results-label") }}
				</span>
			</div>
		</header>

		<section class="records">
			<LoadingOverlay :visible="isLoading" />
			<div class="scroll-container">
				<table v-if="paginatedItems.length || isLoading">
					<thead>
						<tr>
							<th
								v-for="col in orderedHeaderColumns"
								:key="col.key"
								scope="col"
								:class="[col.class, { sortable: col.sortKey != null }]"
								:data-sort-order="col.sortKey ? sortOrderFor(col.sortKey) : undefined"
							>
								<span v-if="col.sortKey && sortLevelFor(col.sortKey) !== null" class="sort-level">
									{{ sortLevelFor(col.sortKey) }}
								</span>
								{{ columnLabel(col.key) }}
							</th>
						</tr>
					</thead>
					<tbody>
						<template v-for="item in paginatedItems" :key="item.id">
							<tr
								:class="{ 'row-expanded': expandedRowId === item.id }"
								@click="hasDetailsPanelContent && toggleRow(item.id)"
							>
								<td v-for="col in orderedHeaderColumns" :key="col.key" :class="col.class">
									<template v-if="col.key === ColumnKey.Address">
										<div class="address-cell">
											<AppIcon
												v-if="hasDetailsPanelContent"
												:name="IconName.ChevronRightSmall"
												class="expand-chevron"
												:class="{ expanded: expandedRowId === item.id }"
												:size="11"
											/>
											<OpportunityCardFlagIcon
												:type="item.type"
												:sales-process-status="item.sales_process_status"
												:launch-date-ms="item.launch_date_ms"
												:is-placeholder="item.isPlaceholder"
											/>
											<button
												class="address-link"
												type="button"
												@click.stop="hasDetailsPanelContent && toggleRow(item.id)"
											>
												{{
													isCoordinateAddress(item.address)
														? ""
														: item.address || t("opportunities-table.unknown-address")
												}}
											</button>
										</div>
									</template>
									<template v-else-if="col.key === ColumnKey.City">{{
										formatMovableColumnValue(ColumnKey.City, item, t)
									}}</template>
									<template v-else-if="col.key === ColumnKey.Category">{{
										formatMovableColumnValue(ColumnKey.Category, item, t)
									}}</template>
									<template v-else-if="col.key === ColumnKey.Price">{{
										formatMovableColumnValue(ColumnKey.Price, item, t)
									}}</template>
									<template v-else-if="col.key === ColumnKey.Status">
										<CellDropdown
											:value="itemStatus(item)"
											:options="statusCellOptions"
											:readonly="!canWriteStatus"
											:clear-label="t('opportunities-table.status.none')"
											@change="handleStatusChange(item, $event)"
										/>
									</template>
									<template v-else-if="col.key === ColumnKey.Priority">
										<CellDropdown
											:value="currentPriority(item)"
											:options="priorityCellOptions"
											:readonly="!canWritePriority"
											:clear-label="t('opportunities-table.priority.undetermined')"
											@change="handlePriorityChange(item, $event)"
										/>
									</template>
									<template v-else-if="col.key === ColumnKey.Stage">
										<CellDropdown
											v-if="item.type === OpportunityType.OpportunityTarget"
											:value="currentStage(item)"
											:options="stageCellOptions"
											:readonly="!canWriteStage(item)"
											:fallback-label="item.stageLabel || undefined"
											@change="handleStageChange(item, $event)"
										/>
										<span v-else class="stage-na">N/A</span>
									</template>
									<template v-else-if="col.key === ColumnKey.AssetType">{{
										formatMovableColumnValue(ColumnKey.AssetType, item, t)
									}}</template>
									<template v-else-if="col.key === ColumnKey.Subcategory">{{
										formatMovableColumnValue(ColumnKey.Subcategory, item, t)
									}}</template>
									<template v-else-if="col.key === ColumnKey.Broker">{{
										formatMovableColumnValue(ColumnKey.Broker, item, t)
									}}</template>
									<template v-else-if="col.key === ColumnKey.Notes">
										<span v-if="isHuntProject && noteCount(item) > 0" class="note-badge">{{ noteCount(item) }}</span>
									</template>
									<template v-else-if="col.key === ColumnKey.Area">{{
										formatUnitColumnValue(ColumnKey.Area, item)
									}}</template>
									<template v-else-if="col.key === ColumnKey.BuildableArea">{{
										formatUnitColumnValue(ColumnKey.BuildableArea, item)
									}}</template>
									<template v-else-if="col.key === ColumnKey.PricePerSqft">{{
										formatUnitColumnValue(ColumnKey.PricePerSqft, item)
									}}</template>
									<template v-else-if="col.key === ColumnKey.PricePerBuildableSqft">{{
										formatUnitColumnValue(ColumnKey.PricePerBuildableSqft, item)
									}}</template>
									<template v-else-if="col.key === ColumnKey.Parcels">
										<span v-if="dedupedLotNumbers(item).length" class="parcels-cell">
											<span class="primary-parcel">{{ dedupedLotNumbers(item)[0] }}</span>
											<span
												v-if="!expandedParcelsIds.has(item.id) && dedupedLotNumbers(item).length > 1"
												class="parcels-count"
											>
												+{{ dedupedLotNumbers(item).length - 1 }}
											</span>
											<span
												v-if="expandedParcelsIds.has(item.id) && dedupedLotNumbers(item).length > 1"
												class="parcels-rest"
											>
												, {{ dedupedLotNumbers(item).slice(1).join(", ") }}
											</span>
											<button
												v-if="dedupedLotNumbers(item).length > 1"
												type="button"
												class="parcels-toggle"
												@click.stop="toggleParcelsExpanded(item.id)"
											>
												{{ expandedParcelsIds.has(item.id) ? t("parcels.hide") : t("parcels.showAll") }}
											</button>
										</span>
									</template>
									<template v-else-if="col.key === ColumnKey.Deal">
										<a
											v-if="item.hubspot_record_id"
											:href="hubspotDealUrl(item.hubspot_record_id)"
											target="_blank"
											rel="noopener"
											class="icon-link"
											:aria-label="t('opportunities-table.columns.deal')"
										>
											<AppIcon :name="IconName.HubspotColor" :size="20" />
										</a>
									</template>
									<template v-else-if="col.key === ColumnKey.Map">
										<a
											:href="googleMapsUrl(item.latitude, item.longitude)"
											target="_blank"
											rel="noopener"
											class="icon-link"
											:aria-label="t('opportunities-table.columns.map')"
										>
											<AppIcon :name="IconName.GoogleMaps" :size="20" />
										</a>
									</template>
								</td>
							</tr>
							<tr v-if="expandedRowId === item.id" class="expanded-row-container">
								<td :colspan="orderedHeaderColumns.length" class="expanded-row-cell">
									<ExpandedRowCompact
										:item="item"
										:detail-keys="detailPanelKeys"
										:show-notes-input="showNotesInput"
										:show-links="showLinks"
										:show-location="showLocation"
										:is-in-cca-project="isInCcaProject"
										:is-personal-notes-context="!isInCcaProject"
										:is-hunt-project="isHuntProject"
										:project-id="expandedRowProjectId"
									/>
								</td>
							</tr>
						</template>
					</tbody>
				</table>

				<div v-if="error" class="error">
					<p>{{ t("opportunities-table.error") }}</p>
					<Button variant="tertiary" size="s" shape="pill" @click="retry">{{ t("opportunities-table.retry") }}</Button>
				</div>
				<p v-else-if="!isLoading && paginatedItems.length === 0" class="empty">{{ t("opportunities-table.empty") }}</p>

				<p v-if="isLoading" class="loading">{{ t("opportunities-table.loading") }}</p>
			</div>
		</section>

		<footer class="pagination-footer">
			<ArkPagination :count="paginationCount" :page="currentPage" @page-change="handlePaginationChange" />
		</footer>
	</div>
</template>

<script lang="ts" setup>
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { onClickOutside } from "@vueuse/core"
import debounce from "lodash.debounce"
import { storeToRefs } from "pinia"
import { computed, inject, onMounted, onUnmounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { useView } from "@/composables/useView"
import ArkPagination from "@/components/ui/ArkPagination.vue"
import { PAGINATION_SIZE } from "@/components/constants"
import OpportunitiesTableFilters from "@/components/filtering/OpportunitiesTableFilters.vue"
import OpportunityCardFlagIcon from "@/components/opportunities/OpportunityCardFlagIcon.vue"
import { OpportunityField, OpportunityStage, OpportunityType, ProjectType } from "@/graphql"
import { EvaluationStatus, type Opportunity, OpportunityPriority, type Project } from "@/model/DataModel"
import { Permission, UseRootStore } from "@/model/RootStore"
import {
	isHuntingMode,
	ProjectStoreKey,
	type ViewConfig,
	type ViewConfigColumn,
	type ViewConfigSortLevel,
} from "@/stores/projectStore"
import { isCoordinateAddress } from "@/utils/format"
import {
	ColumnKey,
	DEFAULT_DETAIL_KEYS,
	DETAILS_PANEL_ONLY_KEYS,
	defaultOpportunityColumnGroup,
	FIELDLESS_COLUMN_KEYS,
	formatMovableColumnValue,
	opportunityColumnsFromSelectedFields,
	opportunityColumnLabel,
	selectedOpportunityColumnFields,
	MOVABLE_KEYS,
	TABLE_ONLY_KEYS,
} from "@/utils/opportunityColumns"
import { resolveOpportunityEvaluationStatus } from "@/utils/opportunityEvaluationStatus"
import { projectDisplayName } from "@/utils/project"
import { stageLabelMap, stageOrder } from "@/utils/opportunityStage"
import { useOpportunityStore } from "@/stores/opportunityStore"
import { type View, ViewScope } from "@/types"
import type { OpportunityBrokerNameSource } from "@/utils/ApiClient"
import Button from "@/components/ui/Button.vue"
import LoadingOverlay from "@/components/ui/LoadingOverlay.vue"
import CellDropdown, { type CellDropdownOption } from "./CellDropdown.vue"
import ColumnSelector from "./ColumnSelector.vue"
import ExpandedRowCompact from "./ExpandedRowCompact.vue"
import SortPanel from "./SortPanel.vue"
import TableFloatingFilterPanel from "./TableFloatingFilterPanel.vue"
import TableSearch from "./TableSearch.vue"

const HUBSPOT_DEAL_URL = import.meta.env.VITE_HUBSPOT_DEAL_ENDPOINT
enum SortKey {
	City = "city",
	Price = "price",
	Area = "superficy",
	BuildableArea = "constructible",
	PricePerSqft = "pricePerSqft",
	PricePerBuildableSqft = "pricePerConstructibleSqft",
	Stage = "stage",
	Priority = "priority",
	Status = "status",
	AssetType = "assetType",
	Subcategory = "subcategory",
	LaunchDate = "launchDate",
}

type ColumnDef = {
	key: string
	class: string
	sortKey?: SortKey
}

const ALL_COLUMNS: ColumnDef[] = [
	{ key: ColumnKey.Address, class: "address" },
	{ key: ColumnKey.Area, class: "area", sortKey: SortKey.Area },
	{ key: ColumnKey.AssetType, class: "asset-type", sortKey: SortKey.AssetType },
	{ key: ColumnKey.Broker, class: "broker" },
	{ key: ColumnKey.BuildableArea, class: "buildable-area", sortKey: SortKey.BuildableArea },
	{ key: ColumnKey.Category, class: "category" },
	{ key: ColumnKey.City, class: "city", sortKey: SortKey.City },
	{ key: ColumnKey.Deal, class: "deal" },
	{ key: ColumnKey.Subcategory, class: "subcategory", sortKey: SortKey.Subcategory },
	{ key: ColumnKey.Map, class: "map" },
	{ key: ColumnKey.Notes, class: "notes" },
	{ key: ColumnKey.Parcels, class: "parcels" },
	{ key: ColumnKey.Price, class: "price", sortKey: SortKey.Price },
	{ key: ColumnKey.PricePerBuildableSqft, class: "price-per-buildable-sqft", sortKey: SortKey.PricePerBuildableSqft },
	{ key: ColumnKey.PricePerSqft, class: "price-per-sqft", sortKey: SortKey.PricePerSqft },
	{ key: ColumnKey.Priority, class: "priority", sortKey: SortKey.Priority },
	{ key: ColumnKey.Stage, class: "stage", sortKey: SortKey.Stage },
	{ key: ColumnKey.Status, class: "status", sortKey: SortKey.Status },
]

const CCA_ONLY_KEYS = new Set<string>([ColumnKey.Stage, ColumnKey.Priority])

const GROUP_TABLE = "table"
const GROUP_DETAILS_PANEL = "details-panel"

function defaultGroupFor(key: string): string {
	return defaultOpportunityColumnGroup(key)
}

const store = UseRootStore()
const opportunityStore = useOpportunityStore()
const { projectOpportunitiesInvalidation } = storeToRefs(opportunityStore)
const opportunityView = useView(ViewScope.OpportunitiesTable)
const opportunityHuntingView = useView(ViewScope.OpportunitiesHuntingTable)

const projectStore = inject(ProjectStoreKey)!()
const { currentProject } = storeToRefs(projectStore)

const isInProject = computed(() => isHuntingMode())

const isInCcaProject = computed(
	() => currentProject.value != null && currentProject.value.type !== ProjectType.Workspace
)

const expandedRowProjectId = computed(() => currentProject.value?.id)

const isHuntProject = computed(() => isHuntingMode())
const activeOpportunityView = computed(() => (isInProject.value ? opportunityHuntingView : opportunityView))

// In a project scope (Workspace or CCA opened via ?project=), the Table only surfaces
// opportunity targets, as if the opportunity-type filter were pinned to "target".
const isInProjectScope = computed(() => isInProject.value)
const nonTargetOpportunityTypes = Object.values(OpportunityType).filter(
	type => type !== OpportunityType.OpportunityTarget
)
const brokerFilterFields = [
	OpportunityField.BrokerFirstName,
	OpportunityField.BrokerLastName,
	OpportunityField.CollaborativeBrokerNames,
]
const fetchView = computed<View>(() => {
	const baseView = activeOpportunityView.value.view.value
	const activeView = {
		...baseView,
		order: sortLevels.value.map(({ key, order }) => ({ field: key, direction: order })),
	}
	const view: View =
		activeView.select?.opportunity == null
			? activeView
			: {
					...activeView,
					select: {
						...activeView.select,
						opportunity: [...new Set([...activeView.select.opportunity, ...brokerFilterFields])],
					},
				}
	if (isInProjectScope.value) {
		return {
			...view,
			filter: {
				...view.filter,
				opportunity: {
					...view.filter?.opportunity,
					opportunityTypes: [OpportunityType.OpportunityTarget],
				},
			},
		}
	}

	// outside a project an empty type filter means "everything except targets", not "no restriction"
	if (view.filter?.opportunity?.opportunityTypes?.length) return view
	return {
		...view,
		filter: {
			...view.filter,
			opportunity: {
				...view.filter?.opportunity,
				opportunityTypes: nonTargetOpportunityTypes,
			},
		},
	}
})
const isWaitingForProject = computed(() => isInProject.value && currentProject.value?.id == null)

// Status: CCA client writes in CCA project; any logged-in user writes in Workspace
const canWriteStatus = computed(() => {
	if (!store.isUserLoggedIn) return false
	if (isInCcaProject.value) return !store.currentUser.has(Permission.OpportunitySensitiveWrite)
	return true
})

// Priority + Stage: broker/coordinator/prospector only (Permission.OpportunitySensitiveWrite)
const canWritePriority = computed(() => store.currentUser.has(Permission.OpportunitySensitiveWrite))
function canWriteStage(item: Opportunity): boolean {
	return item.type === OpportunityType.OpportunityTarget && store.currentUser.has(Permission.OpportunitySensitiveWrite)
}

const priorityIconUrls: Partial<Record<OpportunityPriority, string>> = {
	[OpportunityPriority.Low]: "/icons/generated/opportunity-target-low.png",
	[OpportunityPriority.Medium]: "/icons/generated/opportunity-target-medium.png",
	[OpportunityPriority.High]: "/icons/generated/opportunity-target-high.png",
}

// priority options exclude Undetermined — it's the "clear" state rendered via clearLabel
const priorityCellOptions = computed<CellDropdownOption[]>(() =>
	[OpportunityPriority.High, OpportunityPriority.Medium, OpportunityPriority.Low].map(p => ({
		value: p,
		label: t(`opportunities-table.priority.${p}`),
		iconUrl: priorityIconUrls[p],
	}))
)

const stageCellOptions = computed<CellDropdownOption[]>(() =>
	stageOrder.map((s, i) => ({
		value: s,
		label: `${i}. ${t("opportunities-table.stage." + s)}`,
	}))
)

// maps both "0. Targets" (stageLabelMap format) and "Targets" (HubSpot raw format) to enum
const stageLabelToKey = Object.fromEntries([
	...Object.entries(stageLabelMap).map(([k, v]) => [v, k as OpportunityStage]),
	...Object.entries(stageLabelMap).map(([k, v]) => [v.replace(/^\d+\. /, ""), k as OpportunityStage]),
]) as Partial<Record<string, OpportunityStage>>

function currentStage(item: Opportunity): OpportunityStage | null {
	const fromStore = store.opportunityStage.get(Number(item.id))
	if (fromStore !== undefined) return fromStore
	return stageLabelToKey[item.stageLabel ?? ""] ?? null
}

const statusCellOptions = computed<CellDropdownOption[]>(() => [
	{ value: EvaluationStatus.Like, label: t("opportunities-table.status.like"), iconUrl: "/icons/like.png" },
	{ value: EvaluationStatus.Dislike, label: t("opportunities-table.status.dislike"), iconUrl: "/icons/dislike.png" },
])

async function handleStatusChange(item: Opportunity, status: string | null): Promise<void> {
	const prev = item.status ?? null
	item.status = status as EvaluationStatus | null
	try {
		await store.updateOpportunityEvaluation(Number(item.id), { status }, currentProject.value?.id)
	} catch (e) {
		item.status = prev as EvaluationStatus | null
		console.error(e)
	}
}

function currentPriority(item: Opportunity): string | null {
	const p = store.opportunityPriority.get(Number(item.id)) ?? item.priority ?? null
	return !p || p === OpportunityPriority.Undetermined ? null : p
}

async function handlePriorityChange(item: Opportunity, priority: string | null): Promise<void> {
	const p = (priority as OpportunityPriority | null) ?? OpportunityPriority.Undetermined
	const prev = store.opportunityPriority.get(Number(item.id)) ?? item.priority ?? null
	store.opportunityPriority.set(Number(item.id), p)
	item.priority = p
	try {
		await store.updateOpportunity({ opportunityId: String(item.id), projectId: currentProject.value?.id, priority: p })
	} catch (e) {
		store.opportunityPriority.set(Number(item.id), prev)
		item.priority = prev
		console.error(e)
	}
}

async function handleStageChange(item: Opportunity, stage: string | null): Promise<void> {
	if (stage === null) return
	const s = stage as OpportunityStage
	const prev = (store.opportunityStage.get(Number(item.id)) as OpportunityStage | null | undefined) ?? null
	store.opportunityStage.set(Number(item.id), s)
	item.stageLabel = stageLabelMap[s]
	try {
		await store.updateOpportunity({ opportunityId: String(item.id), projectId: currentProject.value?.id, stage: s })
	} catch (e) {
		store.opportunityStage.set(Number(item.id), prev)
		item.stageLabel = prev != null ? stageLabelMap[prev] : ""
		console.error(e)
	}
}

const canEditColumns = computed(() => true)

function availableColumnKeys(isCca: boolean): string[] {
	const canReadSensitive = store.currentUser.has(Permission.OpportunitySensitiveRead)
	const keys = ALL_COLUMNS.filter(col => {
		if ((!isCca || !canReadSensitive) && CCA_ONLY_KEYS.has(col.key)) return false
		// Deal and Map are in the expanded row
		if (col.key === ColumnKey.Deal || col.key === ColumnKey.Map) return false
		// the notes counter is only relevant in hunting mode
		if (col.key === ColumnKey.Notes && !isHuntProject.value) return false
		return true
	}).map(col => col.key)
	// the details panel's Links/Location/Notes-input sections aren't real columns, but are toggleable the same way
	keys.push(...DETAILS_PANEL_ONLY_KEYS)
	return keys
}

const columnGroups = computed<Record<string, string>>(() => {
	const saved = activeOpportunityView.value.view.value.columnOverrides ?? {}
	return Object.fromEntries(MOVABLE_KEYS.map(key => [key, saved[key]?.group ?? defaultGroupFor(key)]))
})

function defaultColumnConfig(isCca: boolean): ViewConfigColumn[] {
	const keys = availableColumnKeys(isCca)
	// address first, rest alphabetically
	const sorted = [ColumnKey.Address, ...keys.filter(k => k !== ColumnKey.Address).sort()]
	return sorted.map(key => ({ key, visible: true }))
}

const newUiColumnConfig = computed<ViewConfigColumn[]>(() => {
	const selectedColumns = activeOpportunityView.value.view.value.select?.opportunity
	const base =
		selectedColumns == null
			? defaultColumnConfig(isInCcaProject.value)
			: opportunityColumnsFromSelectedFields(selectedColumns, availableColumnKeys(isInCcaProject.value))
	const savedOverrides = activeOpportunityView.value.view.value.columnOverrides ?? {}
	return base.map(c =>
		FIELDLESS_COLUMN_KEYS.includes(c.key as ColumnKey) ? { ...c, visible: savedOverrides[c.key]?.visible ?? true } : c
	)
})

const visibleColumnKeys = computed<string[]>(() => newUiColumnConfig.value.filter(c => c.visible).map(c => c.key))

const orderedHeaderColumns = computed<ColumnDef[]>(() => {
	return visibleColumnKeys.value
		.map(key => ALL_COLUMNS.find(col => col.key === key))
		.filter((col): col is ColumnDef => col != null)
		.filter(col => columnGroups.value[col.key] !== GROUP_DETAILS_PANEL)
})

// fields currently placed in the details panel group and visible, in ExpandedRowCompact's order
const detailPanelKeys = computed<string[]>(() => {
	const visible = new Set(visibleColumnKeys.value)
	return MOVABLE_KEYS.filter(key => columnGroups.value[key] === GROUP_DETAILS_PANEL && visible.has(key))
})

const showNotesInput = computed(() => visibleColumnKeys.value.includes(ColumnKey.NotesInput))
const showLinks = computed(() => visibleColumnKeys.value.includes(ColumnKey.Links))
const showLocation = computed(() => visibleColumnKeys.value.includes(ColumnKey.Location))

// when nothing from "Opportunity details" is toggled on, there's nothing to expand into — hide the row chevron
const hasDetailsPanelContent = computed(
	() => detailPanelKeys.value.length > 0 || showNotesInput.value || showLinks.value || showLocation.value
)

type SelectorColumn = {
	key: string
	label: string
	visible: boolean
	group?: string
	locked?: boolean
	fixedGroup?: boolean
}

function columnLabel(key: string): string {
	return opportunityColumnLabel(key, t, areaUnitSymbol.value, pricePerAreaUnitSymbol.value)
}

function formatUnitColumnValue(key: ColumnKey, item: Opportunity): string {
	return formatMovableColumnValue(key, item, t, areaUnit.value)
}

function toSelectorColumns(cols: ViewConfigColumn[]): SelectorColumn[] {
	return cols.map(c => {
		// with groups shown, Address is listed as a locked item inside "Main table" instead of a separate pinned row
		const isAddress = c.key === ColumnKey.Address
		const isTableOnly = TABLE_ONLY_KEYS.includes(c.key as ColumnKey)
		const isDetailsPanelOnly = DETAILS_PANEL_ONLY_KEYS.includes(c.key as ColumnKey)
		return {
			...c,
			visible: isAddress ? true : c.visible,
			label: columnLabel(c.key),
			group: isTableOnly ? GROUP_TABLE : isDetailsPanelOnly ? GROUP_DETAILS_PANEL : c.group ?? defaultGroupFor(c.key),
			locked: isAddress || undefined,
			fixedGroup: isTableOnly || isDetailsPanelOnly || undefined,
		}
	})
}

const groupLabels = computed(() => ({
	[GROUP_TABLE]: t("opportunities-table.column-selector.section-label"),
	[GROUP_DETAILS_PANEL]: t("opportunities-table.expanded-row.section-details"),
}))

const selectorColumns = computed<SelectorColumn[]>(() => {
	const existing = newUiColumnConfig.value.filter(c => availableColumnKeys(isInCcaProject.value).includes(c.key))
	const newKeys = availableColumnKeys(isInCcaProject.value).filter(k => !existing.some(c => c.key === k))
	const cols = [...existing, ...newKeys.map(key => ({ key, visible: true }))].map(c =>
		MOVABLE_KEYS.includes(c.key as ColumnKey) ? { ...c, group: columnGroups.value[c.key] } : c
	)
	return toSelectorColumns(cols)
})

const defaultSelectorColumns = computed<SelectorColumn[]>(() =>
	toSelectorColumns(defaultColumnConfig(isInCcaProject.value))
)

const columnSelectorOpen = ref(false)
const expandedRowId = ref<string | null>(null)

const expandedParcelsIds = ref(new Set<string>())

// lot_numbers_full can repeat the primary lot number; dedupe so the count/expanded list don't double-count it
function dedupedLotNumbers(item: Opportunity): number[] {
	return Array.from(new Set(item.lot_numbers_full ?? []))
}

function toggleParcelsExpanded(id: string): void {
	const next = new Set(expandedParcelsIds.value)
	if (next.has(id)) next.delete(id)
	else next.add(id)
	expandedParcelsIds.value = next
}

function toggleColumnSelector(): void {
	columnSelectorOpen.value = !columnSelectorOpen.value
	showSortPanel.value = false
	filtersPanelOpen.value = false
}

function toggleRow(id: string): void {
	expandedRowId.value = expandedRowId.value === id ? null : id
}

function toggleSortPanel(): void {
	showSortPanel.value = !showSortPanel.value
	columnSelectorOpen.value = false
	filtersPanelOpen.value = false
}

async function toggleFiltersPanel(): Promise<void> {
	filtersPanelOpen.value = !filtersPanelOpen.value
	columnSelectorOpen.value = false
	showSortPanel.value = false

	if (filtersPanelOpen.value) {
		await advancedFiltersRef.value?.loadGeographicOptions()
	}
}

const activeViewConfig = computed<ViewConfig | null | undefined>(
	() => currentProject.value?.viewConfig as ViewConfig | null | undefined
)
const layerFilteredItems = computed(() =>
	isInProjectScope.value ? items.value.filter(item => item.type === OpportunityType.OpportunityTarget) : items.value
)

async function handleColumnSave(columns: SelectorColumn[]): Promise<void> {
	activeOpportunityView.value.setSelect({
		...activeOpportunityView.value.view.value.select,
		opportunity: selectedOpportunityColumnFields(columns),
	})
	const overrides: View["columnOverrides"] = {}
	for (const c of columns) {
		const isMovable = MOVABLE_KEYS.includes(c.key as ColumnKey)
		const isFieldless = FIELDLESS_COLUMN_KEYS.includes(c.key as ColumnKey)
		if (!isMovable && !isFieldless) continue
		overrides[c.key] = {
			...(isMovable && c.group != null ? { group: c.group } : {}),
			...(isFieldless ? { visible: c.visible } : {}),
		}
	}
	activeOpportunityView.value.setColumnOverrides(overrides)
	columnSelectorOpen.value = false
}

function itemStatus(item: Opportunity): string | null {
	return resolveOpportunityEvaluationStatus(item.id, item.status, store.opportunityEvaluations)
}

function noteCount(item: Opportunity): number {
	const notes = store.opportunityNotes.get(Number(item.id))
	return [notes?.brokerNote, notes?.clientNote].filter(n => n != null).length
}
const { t } = useI18n()
const { areaUnit, areaUnitSymbol, pricePerAreaUnitSymbol } = useUnitSystem()

// all results
const items = ref<Opportunity[]>([])
const brokerNameSources = ref<OpportunityBrokerNameSource[]>([])
// total count from the API
const total = ref(0)
// how many items have been fetched so far
const offset = ref(0)
const isLoading = ref(false)
let latestLoadRequestId = 0
let latestBrokerLoadRequestId = 0
let brokerNameSourcesScope: string | null = null
const error = ref(false)

// multi-level sort — stored in View.order in the new UI and view_config in the legacy UI
const sortLevels = ref<ViewConfigSortLevel[]>([])
const showSortPanel = ref(false)
const filtersPanelOpen = ref(false)
const advancedFiltersRef = ref<InstanceType<typeof OpportunitiesTableFilters> | null>(null)
const columnSelectorWrapperRef = ref<HTMLElement | null>(null)
const sortWrapperRef = ref<HTMLElement | null>(null)
const filtersWrapperRef = ref<HTMLElement | null>(null)

const clickOutsideOptions = { ignore: [".popover-content", ".date-custom-panel"] }
onClickOutside(columnSelectorWrapperRef, () => (columnSelectorOpen.value = false), clickOutsideOptions)
onClickOutside(sortWrapperRef, () => (showSortPanel.value = false), clickOutsideOptions)
onClickOutside(filtersWrapperRef, () => (filtersPanelOpen.value = false), clickOutsideOptions)

watch(
	activeViewConfig,
	viewConfig => {
		if (activeOpportunityView.value.view.value.order != null) return
		sortLevels.value = (viewConfig?.sortLevels as ViewConfigSortLevel[] | undefined) ?? []
	},
	{ immediate: true }
)

watch(
	() => activeOpportunityView.value.view.value.order,
	viewOrder => {
		if (viewOrder == null) return
		sortLevels.value = viewOrder.map(({ field, direction }) => ({ key: field, order: direction ?? "asc" }))
	},
	{ immediate: true, deep: true }
)

function handleSortLevelsUpdate(levels: ViewConfigSortLevel[]): void {
	sortLevels.value = levels
	activeOpportunityView.value.setOrder(levels.map(({ key, order }) => ({ field: key, direction: order })))
}

// address text filter — session-only
const addressSearch = ref("")
const addressOptions = computed(() => [...new Set(items.value.map(i => i.address).filter((a): a is string => !!a))])
const currentPage = computed({
	get: () => store.opportunitiesPage,
	set: page => {
		store.opportunitiesPage = page
	},
})

watch(currentProject, () => {
	addressSearch.value = ""
	expandedRowId.value = null
	currentPage.value = 1
	brokerNameSources.value = []
	brokerNameSourcesScope = null

	if (isWaitingForProject.value) return
	debouncedReset()
})

watch(addressSearch, () => {
	currentPage.value = 1
})

const sortOptions = computed(() => {
	const available = new Set(availableColumnKeys(isInCcaProject.value))
	return [
		...ALL_COLUMNS.filter(col => col.sortKey != null && available.has(col.key)).map(col => ({
			value: col.sortKey!,
			label: columnLabel(col.key),
		})),
		{ value: SortKey.LaunchDate, label: t("opportunities-table.sort-by-date") },
	]
})

function sortOrderFor(key: string): "asc" | "desc" | null {
	const level = sortLevels.value.find(l => l.key === key)
	return level?.order ?? null
}

function sortLevelFor(key: string): number | null {
	const index = sortLevels.value.findIndex(l => l.key === key)
	return index === -1 ? null : index + 1
}

const filteredItems = computed(() => {
	let result = layerFilteredItems.value

	if (addressSearch.value.trim()) {
		const query = addressSearch.value.trim().toLowerCase()
		result = result.filter(item => item.address?.toLowerCase().includes(query))
	}

	return result
})

const paginationCount = computed(() => total.value)
const paginatedItems = computed(() => filteredItems.value)

watch(paginationCount, count => {
	const pageCount = Math.max(Math.ceil(count / PAGINATION_SIZE), 1)

	if (currentPage.value <= pageCount) {
		return
	}

	currentPage.value = pageCount
})

type PaginationChangeDetails = {
	page: number
	pageSize: number
	offset: number
}

async function loadPage(page = currentPage.value): Promise<void> {
	const requestId = ++latestLoadRequestId
	isLoading.value = true
	error.value = false
	void loadBrokerNameSources()

	try {
		const nextOffset = (page - 1) * PAGINATION_SIZE
		const { items: results, total: fetchedTotal } = await opportunityStore.fetchByView(fetchView.value, {
			limit: PAGINATION_SIZE,
			offset: nextOffset,
			// only scope the fetch to the project outside Exploration — currentProject also
			// holds the Workspace's id there, and the project-scoped endpoint surfaces its
			// targets regardless of the opportunity-type filter
			projectId: isInProjectScope.value ? currentProject.value?.id : undefined,
		})

		if (requestId !== latestLoadRequestId) return

		items.value = results
		offset.value = nextOffset
		total.value = fetchedTotal
	} catch (e) {
		if (requestId !== latestLoadRequestId) return
		error.value = true
		console.error(e)
	} finally {
		if (requestId === latestLoadRequestId) isLoading.value = false
	}
}

async function loadBrokerNameSources(): Promise<void> {
	const projectId = isInProjectScope.value ? currentProject.value?.id : undefined
	const scope = projectId ?? ViewScope.OpportunitiesTable

	if (brokerNameSourcesScope === scope) {
		return
	}

	const requestId = ++latestBrokerLoadRequestId
	const brokerView: View = {
		...fetchView.value,
		order: undefined,
		select: {
			...fetchView.value.select,
			opportunity: brokerFilterFields,
		},
	}

	try {
		const sources = await opportunityStore.fetchBrokerNameSourcesByView(brokerView, projectId)

		if (requestId !== latestBrokerLoadRequestId) {
			return
		}

		brokerNameSources.value = sources
		brokerNameSourcesScope = scope
	} catch (error) {
		console.error(error)
	}
}

function retry() {
	loadPage()
}

async function reset() {
	currentPage.value = 1
	offset.value = 0
	total.value = 0

	await loadPage(1)
}

async function handlePaginationChange(details: PaginationChangeDetails): Promise<void> {
	currentPage.value = details.page
	expandedRowId.value = null
	offset.value = details.offset

	await loadPage(details.page)
}

const debouncedReset = debounce(() => {
	reset()
}, 250)

watch(
	() => activeOpportunityView.value.view.value,
	() => {
		currentPage.value = 1
		expandedRowId.value = null
		debouncedReset()
	},
	{ deep: true }
)

watch(projectOpportunitiesInvalidation, invalidation => {
	if (!invalidation || invalidation.projectId !== currentProject.value?.id || isWaitingForProject.value) return
	void loadPage()
})

function hubspotDealUrl(id: string): string {
	return `${HUBSPOT_DEAL_URL}${id}`
}

function googleMapsUrl(lat: number, lng: number): string {
	return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
}

onMounted(() => {
	if (isWaitingForProject.value) {
		return
	}

	loadPage()
})

onUnmounted(() => {
	debouncedReset.cancel()
})
</script>

<style scoped>
.reset-filters-button {
	white-space: nowrap;
	align-self: center;
	line-height: 1;
}

.opportunities-table {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;

	header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		background-color: var(--neutral-0);
		padding: var(--spacing-sm) var(--spacing-xs);
		margin-bottom: var(--spacing-xs);
		border-radius: var(--border-radius-lg);
		gap: var(--spacing-md);
		color: var(--neutral-900);
		font-weight: var(--font-weight-medium);

		.toolbar {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
		}

		.header-right {
			display: flex;
			align-items: center;
			gap: var(--spacing-md);
			flex-shrink: 0;
		}

		.total {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
			white-space: nowrap;
			font-size: var(--font-size-small);
			color: var(--neutral-600);

			.project-name {
				font-weight: var(--font-weight-semibold);
				color: var(--neutral-900);
			}

			.total-separator {
				color: var(--neutral-600);
			}

			strong {
				font-weight: var(--font-weight-semibold);
			}
		}

		.column-selector-anchor,
		.sort-wrapper,
		.filters-wrapper {
			position: relative;
		}

		.filters-toggle,
		.sort-toggle {
			&.active {
				border-color: var(--neutral-900);
			}
		}

		.filters-toggle,
		.sort-toggle,
		.column-selector-toggle {
			.count {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				min-width: var(--space-16);
				height: var(--space-16);
				margin-left: var(--spacing-xs);
				padding: 0 var(--spacing-xxs);
				border-radius: var(--border-radius-full);
				background: var(--neutral-200);
				color: var(--neutral-900);
				font-size: var(--font-size-xxs);
				font-weight: var(--font-weight-semibold);
			}
		}
	}

	.records {
		position: relative;
		flex: 0 1 auto;
		min-height: 0;

		.scroll-container {
			height: 100%;
			overflow: auto;
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-lg);

			table {
				background-color: var(--neutral-0);
				width: 100%;
				min-width: max-content;
				table-layout: fixed;
				font-size: var(--font-size-small);
				border-collapse: separate;
				border-spacing: 0;

				:is(thead, tbody) tr :is(th, td) {
					padding: var(--space-10);
					border-bottom: var(--border-width-thin) solid var(--neutral-100);
					overflow: hidden;
					white-space: nowrap;
					text-overflow: ellipsis;
				}

				td.expanded-row-cell {
					overflow: visible;
					white-space: normal;
					background-color: var(--neutral-50);
				}

				td.status,
				td.priority,
				td.stage {
					overflow: visible;
				}

				td.parcels {
					overflow: visible;
					white-space: normal;
				}

				thead tr th {
					position: sticky;
					top: 0;
					z-index: 1;
					font-size: var(--font-size-xs);
					font-weight: var(--font-weight-medium);
					text-align: left;
					text-transform: uppercase;
					letter-spacing: 0.02em;
					background-color: var(--neutral-0);
					color: var(--neutral-600);
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					border-bottom: var(--border-width-thin) solid var(--neutral-200);

					&.sortable {
						cursor: default;
						user-select: none;

						&::after {
							content: "↕";
							margin-left: var(--space-4);
							opacity: 0.4;
							font-size: var(--font-size-small);
						}

						&:not([data-sort-order])::after {
							display: none;
						}

						&[data-sort-order="asc"]::after {
							content: "↑";
							opacity: 0.8;
						}

						&[data-sort-order="desc"]::after {
							content: "↓";
							opacity: 0.8;
						}

						.sort-level {
							display: inline-flex;
							align-items: center;
							justify-content: center;
							min-width: var(--space-16);
							height: var(--space-16);
							margin-right: var(--spacing-xs);
							padding: 0 var(--spacing-xxs);
							border-radius: var(--border-radius-full);
							background: var(--neutral-200);
							color: var(--neutral-900);
							font-size: var(--font-size-xxs);
							font-weight: var(--font-weight-semibold);
							vertical-align: middle;
						}

						.col-name {
							display: inline-block;
						}
					}
				}

				:is(th, td).address {
					width: var(--width-table-col-address);
				}

				:is(th, td).city,
				:is(th, td).price,
				:is(th, td).category {
					width: var(--width-small-input);
				}

				:is(th, td).area,
				:is(th, td).buildable-area,
				:is(th, td).price-per-sqft,
				:is(th, td).price-per-buildable-sqft {
					width: var(--space-80);
				}

				:is(th, td).parcels {
					width: var(--width-table-col-parcels);
				}

				:is(th, td).broker,
				:is(th, td).stage,
				:is(th, td).asset-type,
				:is(th, td).subcategory {
					width: var(--width-small-input);
				}

				td.stage .stage-na {
					color: var(--neutral-600);
					font-size: var(--font-size-small);
					padding: 0 var(--space-4);
				}

				:is(th, td).notes {
					width: var(--width-table-col-icon);
					text-align: center;
				}

				:is(th, td).status,
				:is(th, td).priority,
				:is(th, td).deal,
				:is(th, td).map {
					width: var(--width-table-col-icon);
					text-align: center;
					padding: var(--spacing-xs);
				}

				tbody {
					background-color: var(--neutral-0);

					tr:hover {
						background-color: var(--neutral-50);
					}

					td {
						text-align: left;

						&:empty::after {
							content: "—";
						}
					}

					.address-cell {
						display: flex;
						align-items: center;
						gap: var(--space-6);
						min-width: 0;

						img {
							height: var(--size-icon-xl);
							width: var(--size-icon-xl);
							object-fit: contain;
							flex-shrink: 0;
						}

						.expand-chevron {
							flex-shrink: 0;
							color: var(--neutral-600);
							transition: transform 0.15s ease;

							&.expanded {
								transform: rotate(90deg);
							}
						}
					}

					.address-link {
						background: none;
						border: none;
						padding: 0;
						cursor: pointer;
						color: var(--neutral-900);
						text-decoration: none;
						font-family: inherit;
						font-size: inherit;
						text-align: left;
						overflow: hidden;
						white-space: nowrap;
						text-overflow: ellipsis;
					}

					.note-badge {
						display: inline-flex;
						align-items: center;
						justify-content: center;
						min-width: 18px;
						height: 18px;
						padding: 0 4px;
						border-radius: var(--border-radius-full);
						background: var(--brand-green);
						color: var(--neutral-0);
						font-size: var(--font-size-xs);
						font-weight: var(--font-weight-medium);
						font-variant-numeric: tabular-nums;
						line-height: 1;
					}

					.parcels-cell {
						display: inline;

						.parcels-count {
							font-size: var(--font-size-xxs);
							color: var(--neutral-600);
						}

						.parcels-toggle {
							display: inline;
							margin-left: var(--space-4);
							background: none;
							border: none;
							padding: 0;
							font: inherit;
							font-size: var(--font-size-xxs);
							font-weight: var(--font-weight-semibold);
							color: var(--neutral-600);
							cursor: pointer;

							&:hover {
								color: var(--primary-green);
							}
						}
					}
				}
			}

			.icon-link {
				text-decoration: none;
				display: inline-flex;
				align-items: center;
			}

			.loading,
			.empty,
			.error {
				padding: var(--spacing-lg);
				text-align: center;
				color: var(--neutral-600);
			}

			.error {
				color: var(--accent-warning);

				button {
					margin-top: var(--spacing-md);
				}
			}
		}
	}

	.pagination-footer {
		display: flex;
		flex-shrink: 0;
		padding: var(--spacing-md) var(--spacing-xs) 0;
	}
}
</style>
